<script>
/**
 * KRX 원천 전문 — HQ 전용.
 *
 * 수신한 모든 데이터 전문이 `krx.frame` 에 정의서상 한글 항목명 그대로 쌓인다.
 * 체결·종목처럼 전용 표를 가진 것도 여기 먼저 들어가고, 전용 표가 없는 것
 * (우선호가·시세 스냅샷·통계 등)은 **여기에만** 있다.
 *
 * 이 화면 하나가 전용 화면이 없는 전문 전부를 대신한다. 전문 종류마다 화면을
 * 만들면 열 개가 넘는데 대부분은 1년에 몇 번 볼까 말까다.
 *
 * 맨 위의 종류별 집계가 핵심이다. **코스콤이 실제로 무엇을 보내는지**가 거기서
 * 드러난다 — 특히 체결이 A301G 로 오는지 G701G(체결+우선호가)로 오는지.
 * G701G 로 오면 krx.trade 가 비는데, 시세 화면만 보면 "데이터가 안 온다"로 오해한다.
 */
import { getKrxMeta, getKrxFrameTypes, getKrxFrames, getKrxFrame } from '@/api/krxMarket.js'

/**
 * 전문 종류 설명. 화면에 보여줄 이름일 뿐 판단에 쓰지 않는다.
 * 실제 레이아웃과 길이는 수신서버의 layouts.tsv 가 들고 있다.
 */
const TYPE_LABEL = {
  A001G: { name: '금현물 종목정보', len: 413, screen: '시세 → 종목' },
  A301G: { name: '금현물 체결', len: 163, screen: '시세 → 체결·현재가' },
  A501G: { name: '임의종료', len: 35, screen: null },
  A601G: { name: '종목마감', len: 112, screen: null },
  A701G: { name: '장운영TS', len: 68, screen: '수신 현황 → 장 시간' },
  B201G: { name: '금현물 시세 snapshot', len: 854, screen: null },
  B601G: { name: '금현물 우선호가', len: 795, screen: null },
  C101G: { name: '종목별 투자자별 종가통계', len: 96, screen: null },
  C401G: { name: '금현물 협의거래 결과', len: 98, screen: null },
  G701G: { name: '금현물 체결 + 우선호가', len: 887, screen: null },
  IC01G: { name: '상품별 투자자별 통계', len: 84, screen: null },
  M401G: { name: '장운영스케줄공개', len: 83, screen: '수신 현황 → 장 시간' },
  R101G: { name: '금현물 장운영TS + 우선호가', len: 815, screen: null },
  IA000: { name: '금현물지수 (스펙 미수령)', len: null, screen: null },
  IB000: { name: '금현물예상지수 (스펙 미수령)', len: null, screen: null },
}

export default {
  name: 'KrxFramePage',

  data() {
    return {
      serverIds: [],
      search: { bizDate: '', serverId: '', msgType: '', isin: '' },
      types: [],
      frames: { items: [], total: 0, page: 1, size: 50, totalPages: 0 },
      loading: false,
      errorMessage: '',

      detailDialog: false,
      detail: null,
      detailLoading: false,
      fieldFilter: '',
    }
  },

  computed: {
    totalFrames() {
      return this.types.reduce((a, t) => a + Number(t.frameCount || 0), 0)
    },
    /** 상세의 항목을 검색어로 거른다. 80여 필드라 눈으로 찾기 어렵다. */
    detailFields() {
      if (!this.detail?.fields) return []
      const q = this.fieldFilter.trim().toLowerCase()
      return Object.entries(this.detail.fields)
        .map(([name, value]) => ({ name, value }))
        .filter(f => !q || f.name.toLowerCase().includes(q)
          || String(f.value).toLowerCase().includes(q))
    },
  },

  async mounted() {
    await this.loadMeta()
    await this.refresh()
  },

  methods: {
    async loadMeta() {
      try {
        const res = await getKrxMeta()
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '초기 정보를 가져오지 못했습니다.'
          return
        }
        const d = res.data.data || {}
        this.serverIds = d.serverIds || []
        // 체결이 아니라 원천이 들어온 마지막 날을 쓴다. 체결 전용 전문이
        // 아닌 형태로 오면 체결 기준 날짜가 엉뚱해진다.
        this.search.bizDate = d.defaultFrameDate || d.defaultBizDate || ''
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '초기 정보를 가져오지 못했습니다.'
      }
    },

    async refresh() {
      this.loading = true
      this.errorMessage = ''
      try {
        await Promise.all([this.fetchTypes(), this.fetchFrames(1)])
      } finally {
        this.loading = false
      }
    },

    async fetchTypes() {
      const res = await getKrxFrameTypes({
        bizDate: this.search.bizDate || undefined,
        serverId: this.search.serverId || undefined,
      })
      if (res.data?.code === 0) {
        this.types = res.data.data || []
      }
    },

    async fetchFrames(page = 1) {
      const res = await getKrxFrames({
        bizDate: this.search.bizDate || undefined,
        serverId: this.search.serverId || undefined,
        msgType: this.search.msgType || undefined,
        isin: this.search.isin || undefined,
        page,
        size: this.frames.size,
      })
      if (res.data?.code === 0) {
        this.frames = { ...res.data.data, size: this.frames.size }
      } else {
        this.errorMessage = res.data?.message || '전문 조회에 실패했습니다.'
      }
    },

    /** 집계 카드를 누르면 그 종류만 본다. */
    pickType(msgType) {
      this.search.msgType = this.search.msgType === msgType ? '' : msgType
      this.fetchFrames(1)
    },

    async openDetail(row) {
      this.detail = null
      this.fieldFilter = ''
      this.detailDialog = true
      this.detailLoading = true
      try {
        const res = await getKrxFrame(row.id, row.bizDate)
        if (res.data?.code === 0) {
          this.detail = res.data.data
        } else {
          this.errorMessage = res.data?.message || '전문 상세 조회에 실패했습니다.'
          this.detailDialog = false
        }
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '전문 상세 조회 중 오류가 발생했습니다.'
        this.detailDialog = false
      } finally {
        this.detailLoading = false
      }
    },

    typeName(t) {
      return TYPE_LABEL[t]?.name || '(정의서에 없는 전문)'
    },
    typeScreen(t) {
      return TYPE_LABEL[t]?.screen || null
    },
    /** 정의서 길이와 실제 길이가 다르면 레이아웃이 어긋난 것이다. */
    lenMismatch(row) {
      const spec = TYPE_LABEL[row.msgType]?.len
      return spec != null && row.frameLen != null && spec !== row.frameLen
    },
    specLen(t) {
      return TYPE_LABEL[t]?.len
    },

    formatNumber(v) {
      if (v === null || v === undefined) return '-'
      return Number(v).toLocaleString('ko-KR')
    },
    formatTime(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleTimeString('ko-KR', { hour12: false })
    },
    formatDateTime(iso) {
      if (!iso) return '-'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      return d.toLocaleString('ko-KR', { hour12: false })
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">KRX 원천 전문</div>
        <div class="text-body-2 text-grey mt-1">
          수신한 모든 전문을 정의서 항목명 그대로 본다
        </div>
      </div>
      <v-spacer />
      <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
        <v-icon start>mdi-refresh</v-icon>새로고침
      </v-btn>
    </div>

    <!-- 검색 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.bizDate"
              type="date"
              label="영업일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:170px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-select
              v-model="search.serverId"
              :items="[{ title: '전체', value: '' }, ...serverIds.map(s => ({ title: s, value: s }))]"
              label="수신 서버"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:150px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.isin"
              label="종목코드"
              placeholder="KR7...."
              density="compact"
              variant="outlined"
              hide-details
              clearable
              style="min-width:190px;"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" variant="flat" :loading="loading" @click="refresh">
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- 전문 종류별 집계 — 이 화면의 핵심 -->
    <v-card rounded="lg" elevation="0" border class="mb-4">
      <v-card-title class="pa-4 pb-2 d-flex align-center flex-wrap">
        <span class="text-subtitle-2 font-weight-bold">전문 종류별</span>
        <span class="text-caption text-grey ml-2">
          합계 {{ formatNumber(totalFrames) }}건 · {{ types.length }}종
        </span>
        <v-spacer />
        <v-chip v-if="search.msgType" closable size="small" color="primary" variant="flat"
                @click:close="pickType(search.msgType)">
          {{ search.msgType }} 만 보는 중
        </v-chip>
      </v-card-title>
      <v-divider />
      <v-table density="compact">
        <thead>
          <tr>
            <th style="width:90px;">전문</th>
            <th>이름</th>
            <th class="text-right" style="width:110px;">건수</th>
            <th class="text-right" style="width:80px;">종목</th>
            <th class="text-right" style="width:110px;">길이</th>
            <th style="width:100px;">첫 수신</th>
            <th style="width:100px;">마지막</th>
            <th style="width:180px;">전용 화면</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="t in types"
            :key="t.msgType"
            style="cursor:pointer;"
            :class="search.msgType === t.msgType ? 'admin-row-selected' : ''"
            @click="pickType(t.msgType)"
          >
            <td class="text-caption font-weight-bold">{{ t.msgType }}</td>
            <td class="text-body-2">{{ typeName(t.msgType) }}</td>
            <td class="text-right font-weight-medium">{{ formatNumber(t.frameCount) }}</td>
            <td class="text-right text-caption">{{ formatNumber(t.isinCount) }}</td>
            <td class="text-right text-caption">
              {{ formatNumber(t.frameLen) }}
              <span v-if="specLen(t.msgType) && specLen(t.msgType) !== t.frameLen"
                    class="text-error font-weight-bold">
                ≠{{ specLen(t.msgType) }}
              </span>
            </td>
            <td class="text-caption">{{ formatTime(t.firstRecvAt) }}</td>
            <td class="text-caption">{{ formatTime(t.lastRecvAt) }}</td>
            <td class="text-caption">
              <span v-if="typeScreen(t.msgType)" class="text-success">
                {{ typeScreen(t.msgType) }}
              </span>
              <span v-else class="text-grey">이 화면에서만</span>
            </td>
          </tr>
          <tr v-if="types.length === 0">
            <td colspan="8" class="text-center text-grey py-8">
              이 영업일에 수신된 전문이 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- 전문 목록 -->
    <v-card rounded="lg" elevation="0" border>
      <v-card-title class="pa-4 pb-2">
        <span class="text-subtitle-2 font-weight-bold">전문 목록</span>
        <span class="text-caption text-grey ml-2">{{ formatNumber(frames.total) }}건</span>
        <v-spacer />
        <span class="text-caption text-grey">행을 누르면 전체 항목을 봅니다</span>
      </v-card-title>
      <v-divider />
      <v-table density="compact">
        <thead>
          <tr>
            <th style="width:170px;">수신시각</th>
            <th style="width:80px;">전문</th>
            <th>종목</th>
            <th style="width:110px;">일련번호</th>
            <th style="width:70px;">보드</th>
            <th style="width:70px;">세션</th>
            <th class="text-right" style="width:80px;">길이</th>
            <th style="width:110px;">수신기</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="f in frames.items"
            :key="f.id"
            style="cursor:pointer;"
            @click="openDetail(f)"
          >
            <td class="text-caption">{{ formatDateTime(f.recvAt) }}</td>
            <td class="text-caption font-weight-bold">{{ f.msgType }}</td>
            <td class="text-caption">{{ f.isin || '-' }}</td>
            <td class="text-caption">{{ f.seq != null ? formatNumber(f.seq) : '-' }}</td>
            <td class="text-caption">{{ f.boardId || '-' }}</td>
            <td class="text-caption">{{ f.sessionId || '-' }}</td>
            <td class="text-right text-caption" :class="lenMismatch(f) ? 'text-error font-weight-bold' : ''">
              {{ formatNumber(f.frameLen) }}
            </td>
            <td class="text-caption text-grey">
              {{ f.serverId }}
              <v-chip v-if="f.source === 'RECOVERY'" size="x-small" color="warning" variant="flat">
                복구
              </v-chip>
            </td>
          </tr>
          <tr v-if="frames.items.length === 0">
            <td colspan="8" class="text-center text-grey py-8">조회된 전문이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <v-divider v-if="frames.totalPages > 1" />
      <div v-if="frames.totalPages > 1" class="pa-3">
        <v-pagination
          :model-value="frames.page"
          :length="frames.totalPages"
          density="compact"
          total-visible="7"
          @update:model-value="fetchFrames"
        />
      </div>
    </v-card>

    <!-- 상세 -->
    <v-dialog v-model="detailDialog" max-width="820" scrollable>
      <v-card rounded="lg">
        <v-card-title class="pa-4 pb-3">
          <div class="d-flex align-center flex-wrap">
            <span class="text-subtitle-1 font-weight-bold mr-2">
              {{ detail?.msgType }} {{ detail ? typeName(detail.msgType) : '' }}
            </span>
            <v-chip v-if="detail" size="x-small" variant="tonal" class="mr-1">
              {{ detail.frameLen }}B
            </v-chip>
            <v-chip v-if="detail?.isin" size="x-small" variant="tonal">{{ detail.isin }}</v-chip>
          </div>
          <div v-if="detail" class="text-caption text-grey mt-1">
            {{ formatDateTime(detail.recvAt) }} · {{ detail.serverId }} · {{ detail.source }}
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height:60vh;">
          <div v-if="detailLoading" class="d-flex justify-center py-10">
            <v-progress-circular indeterminate color="amber-darken-2" size="36" />
          </div>
          <template v-else-if="detail">
            <v-text-field
              v-model="fieldFilter"
              label="항목 검색"
              placeholder="예: 매도1단계"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              class="mb-3"
            />
            <v-table density="compact">
              <thead>
                <tr>
                  <th style="width:52%;">항목명</th>
                  <th>값</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in detailFields" :key="f.name">
                  <td class="text-caption">{{ f.name }}</td>
                  <td class="text-body-2 font-weight-medium">{{ f.value }}</td>
                </tr>
                <tr v-if="detailFields.length === 0">
                  <td colspan="2" class="text-center text-grey py-6">
                    해당하는 항목이 없습니다.
                  </td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <span class="text-caption text-grey ml-2">
            항목 순서는 저장 순서입니다 — 정의서 순서가 아닙니다
          </span>
          <v-spacer />
          <v-btn variant="text" @click="detailDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
